import SectionTitle from "@/components/ui/section-title";

export default function Privacy() {
  return (
    <section className="py-24 bg-gradient-to-br from-white to-purple-50">
      <div className="container mx-auto px-4">
        <SectionTitle 
          title="Privacy Policy"
        />

      <div className="text-sm text-gray-600 mb-6">
        <p>Effective Date: [Insert Date]</p>
        <p>Last Updated: [Insert Date]</p>
      </div>

      <p className="mb-6">
        GrapeGraph.ai ("we", "our", or "us") respects your privacy and is committed to protecting your personal information. This
        Privacy Policy describes how we collect, use, disclose, and store your information when you use our platform, in
        accordance with the Personal Information Protection and Electronic Documents Act (PIPEDA) and other applicable
        Canadian privacy laws.
      </p>

      <div className="space-y-6">
        <section>
          <h2 className="text-xl font-semibold mb-2">1. Information We Collect</h2>
          <div className="space-y-3">
            <div>
              <h3 className="font-medium">a. Personal Information You Provide</h3>
              <ul className="list-disc ml-6 space-y-1">
                <li>Name, email address, and account credentials when you register</li>
                <li>Payment information (processed by third-party providers)</li>
                <li>Scripts, uploaded images, or voice data you input into the platform</li>
                <li>Support inquiries or communication with us</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium">b. Automatically Collected Information</h3>
              <ul className="list-disc ml-6 space-y-1">
                <li>IP address, browser type, device type, operating system</li>
                <li>Log data, usage activity, and interaction with features</li>
                <li>Cookies and similar tracking technologies (see Section 7)</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">2. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Provide, maintain, and improve the GrapeGraph.ai platform</li>
            <li>Personalize your experience and support character creation</li>
            <li>Communicate with you about your account, updates, and promotions</li>
            <li>Analyze usage and improve AI model performance</li>
            <li>Comply with legal obligations and enforce our Terms of Service</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">3. Sharing of Information</h2>
          <p>We do not sell your personal information. We may share your data with:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Service providers: Payment processors, cloud hosting, analytics tools, etc. (only as necessary)</li>
            <li>Legal authorities: If required to comply with legal processes or enforce our rights</li>
            <li>Business transfers: In the event of a merger, acquisition, or sale</li>
          </ul>
          <p className="mt-2">All third-party services are contractually obligated to protect your data consistent with PIPEDA.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">4. AI-Generated Content and Your Input Data</h2>
          <p>When you input scripts, character designs, or voice data into the platform:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>You retain ownership of your original content</li>
            <li>We may use de-identified or aggregated data to train and improve our AI systems, unless you opt out</li>
            <li>You must ensure you have the right to use and upload any data you submit</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">5. Data Retention</h2>
          <p>We retain your personal information only as long as necessary for:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>The purposes set out in this policy</li>
            <li>Legal or contractual requirements</li>
          </ul>
          <p className="mt-2">You may request account deletion at any time via <a href="mailto:support@grapegraph.ai" className="text-purple-600 hover:underline">support@grapegraph.ai</a></p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">6. Your Rights (Under PIPEDA)</h2>
          <p>You have the right to:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Access your personal data held by us</li>
            <li>Request corrections to inaccurate or outdated information</li>
            <li>Withdraw consent for certain data uses</li>
            <li>File a complaint with the Office of the Privacy Commissioner of Canada (OPC)</li>
          </ul>
          <p className="mt-2">To exercise these rights, contact us at <a href="mailto:privacy@grapegraph.ai" className="text-purple-600 hover:underline">privacy@grapegraph.ai</a>.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">7. Cookies and Tracking</h2>
          <p>We use cookies to:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Maintain session data</li>
            <li>Track usage analytics</li>
            <li>Personalize features</li>
          </ul>
          <p className="mt-2">You can control cookies through your browser settings, but some features may not work properly if cookies are disabled.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">8. Data Storage and Security</h2>
          <p>Your data is stored securely on servers located in [Canada/USA] and protected using:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Encryption in transit and at rest</li>
            <li>Access controls and firewalls</li>
            <li>Regular security reviews and monitoring</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">9. Children's Privacy</h2>
          <p>Our platform is not intended for children under the age of 13. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such data, please contact us.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">10. Changes to This Privacy Policy</h2>
          <p>We may update this policy from time to time. Significant changes will be posted on our website and, where appropriate, emailed to users.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">11. Contact Us</h2>
          <div>
            <p>If you have any questions or concerns about this Privacy Policy or our data practices, please contact:</p>
            <div className="mt-2 ml-4">
              <p>Privacy Officer</p>
              <p>GrapeGraph.ai</p>
              <p>Toronto, Ontario, Canada</p>
              <p><a href="mailto:privacy@grapegraph.ai" className="text-purple-600 hover:underline">privacy@grapegraph.ai</a></p>
            </div>
          </div>
        </section>
      </div>


      </div>
    </section>
  );
}
